// File: frontend/src/views/public/Pricing.js

import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../hooks/useLanguage';
import { useNavigate } from 'react-router-dom';
import { Check, X, ArrowRight, Shield, Lock, Zap } from 'lucide-react';
import AuthModal from '../../components/auth/AuthModal';
import PageContainer from '../../components/layout/PageContainer';
import { PRICING_TIERS, ALL_TIERS } from '../../config/pricing';
import styles from './Pricing.module.css';
import SEO from '../../components/utility/SEO';
import { SEO_CONFIG } from '../../config/seo';

const Pricing = () => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const seo = SEO_CONFIG.pricing;

  const handleGetStarted = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    navigate('/dashboard');
  };

  // Feature comparison matrix
  const FEATURE_CATEGORIES = [
    {
      name: t('pricing.features.storage.title', 'Storage & Access'),
      features: [
        { name: t('pricing.features.storage.unlimited', 'Unlimited policy storage'), tiers: ['free', 'smart', 'complete', 'business'] },
        { name: t('pricing.features.storage.mobile', 'Mobile app access'), tiers: ['free', 'smart', 'complete', 'business'] },
        { name: t('pricing.features.storage.vault', 'Document vault'), tiers: { complete: '5GB', business: '25GB' } },
      ]
    },
    {
      name: t('pricing.features.ai.title', 'AI Assistant'),
      features: [
        { name: t('pricing.features.ai.questions', 'AI questions per month'), tiers: { free: '5', smart: 'Unlimited', complete: 'Unlimited', business: 'Unlimited' } },
        { name: t('pricing.features.ai.insights', 'Policy insights'), tiers: ['free', 'smart', 'complete', 'business'] },
        { name: t('pricing.features.ai.health_score', 'Coverage health score'), tiers: ['smart', 'complete', 'business'] },
        { name: t('pricing.features.ai.claims', 'AI Claims assistant'), tiers: ['complete', 'business'] },
        { name: t('pricing.features.ai.risk', 'AI Risk Manager'), tiers: ['business'] },
      ]
    },
    {
      name: t('pricing.features.alerts.title', 'Alerts & Monitoring'),
      features: [
        { name: t('pricing.features.alerts.renewal', 'Renewal alerts (90/60/30 days)'), tiers: ['free', 'smart', 'complete', 'business'] },
        { name: t('pricing.features.alerts.rate', 'Rate comparison alerts'), tiers: ['smart', 'complete', 'business'] },
        { name: t('pricing.features.alerts.gap', 'Coverage gap detection'), tiers: ['smart', 'complete', 'business'] },
        { name: t('pricing.features.alerts.quarterly', 'Quarterly rate comparison'), tiers: ['complete', 'business'] },
      ]
    },
    {
      name: t('pricing.features.sharing.title', 'Sharing & Collaboration'),
      features: [
        { name: t('pricing.features.sharing.users', 'Share with users'), tiers: { free: '—', smart: '2', complete: '4', business: '10' } },
      ]
    },
    {
      name: t('pricing.features.support.title', 'Support'),
      features: [
        { name: t('pricing.features.support.email', 'Email support'), tiers: ['free', 'smart', 'complete', 'business'] },
        { name: t('pricing.features.support.priority_email', 'Priority email support'), tiers: ['smart', 'complete', 'business'] },
        { name: t('pricing.features.support.phone', 'Priority phone support'), tiers: ['complete', 'business'] },
        { name: t('pricing.features.support.account_manager', 'Dedicated account manager'), tiers: ['business'] },
      ]
    },
    {
      name: t('pricing.features.advanced.title', 'Advanced Features'),
      features: [
        { name: t('pricing.features.advanced.api', 'API access'), tiers: ['business'] },
        { name: t('pricing.features.advanced.integrations', 'Custom integrations'), tiers: ['business'] },
        { name: t('pricing.features.advanced.white_label', 'White-label options'), tiers: ['business'] },
      ]
    }
  ];

  const FAQ_ITEMS = [
    {
      question: t('pricing.faq.q1', 'Can I switch plans anytime?'),
      answer: t('pricing.faq.a1', 'Yes. Upgrade instantly and start using new features right away. Downgrades take effect at the end of your current billing period.')
    },
    {
      question: t('pricing.faq.q2', 'What payment methods do you accept?'),
      answer: t('pricing.faq.a2', 'We accept all major credit cards (Visa, Mastercard, Amex) and debit cards through Stripe. We never see or store your payment details.')
    },
    {
      question: t('pricing.faq.q3', 'Is there a free trial?'),
      answer: t('pricing.faq.a3', 'The Free Forever plan requires no credit card and never expires. Paid plans include a 14-day money-back guarantee — try risk-free.')
    },
    {
      question: t('pricing.faq.q4', 'What happens if I cancel?'),
      answer: t('pricing.faq.a4', 'You keep access until the end of your billing period. After that, your account reverts to the Free Forever plan. You can reactivate anytime.')
    },
    {
      question: t('pricing.faq.q5', 'Do you offer refunds?'),
      answer: t('pricing.faq.a5', 'Yes. Full refund within 14 days of purchase, no questions asked. After 14 days, we prorate refunds based on unused time.')
    },
    {
      question: t('pricing.faq.q6', 'Can I get a discount for annual billing?'),
      answer: t('pricing.faq.a6', 'Yes! Annual plans save 17% compared to monthly. That\'s like getting 2 months free every year.')
    }
  ];

  return (
    <>
      <SEO 
        title={seo.title}    
        description={seo.description}
        canonical={seo.canonical}
        image={seo.image}
      />
      <PageContainer>
        <div className={styles.page}>
          {/* Hero Section */}
          <section className={styles.hero}>
            <div className={styles.heroContent}>
              <h1 className={styles.heroTitle}>
                {t('pricing.hero.title', 'Choose Your Plan')}
              </h1>
              <p className={styles.heroSubtitle}>
                {t('pricing.hero.subtitle', 'Start free. Upgrade when you need more. Cancel anytime. All plans include end-to-end encryption and 24/7 access to your policies.')}
              </p>

              {/* Billing Toggle */}
              <div className={styles.billingToggle}>
                <button
                  className={`${styles.toggleButton} ${billingCycle === 'monthly' ? styles.active : ''}`}
                  onClick={() => setBillingCycle('monthly')}
                >
                  {t('pricing.billing.monthly', 'Monthly')}
                </button>
                <button
                  className={`${styles.toggleButton} ${billingCycle === 'yearly' ? styles.active : ''}`}
                  onClick={() => setBillingCycle('yearly')}
                >
                  {t('pricing.billing.yearly', 'Yearly')}
                  <span className={styles.savingsBadge}>
                    {t('pricing.billing.save', 'Save 17%')}
                  </span>
                </button>
              </div>
            </div>
          </section>

          {/* Pricing Cards */}
          <section className={styles.pricingSection}>
            <div className={styles.pricingGrid}>
              {ALL_TIERS.map((tierId) => {
                const tier = PRICING_TIERS[tierId];
                const price = billingCycle === 'monthly' ? tier.price.monthly : tier.price.yearly;

                return (
                  <div key={tier.id} className={styles.pricingCard}>
                    <div className={styles.cardHeader}>
                      <h3 className={styles.tierName}>{tier.name}</h3>
                      <p className={styles.tierTagline}>{tier.tagline}</p>
                    </div>

                    <div className={styles.priceContainer}>
                      {price === 0 ? (
                        <div className={styles.freePrice}>
                          {t('pricing.free', 'Free')}
                        </div>
                      ) : (
                        <>
                          <div className={styles.price}>
                            <span className={styles.currency}>$</span>
                            <span className={styles.amount}>{price.toFixed(0)}</span>
                            <span className={styles.period}>
                              {billingCycle === 'monthly' 
                                ? t('pricing.per_month', '/month')
                                : t('pricing.per_year', '/year')
                              }
                            </span>
                          </div>
                          {billingCycle === 'yearly' && (
                            <div className={styles.yearlyNote}>
                              {t('pricing.monthly_equivalent', '$monthly/month', {
                                monthly: (price / 12).toFixed(2)
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    <button
                      className={styles.ctaButton}
                      onClick={handleGetStarted}
                    >
                      {t('pricing.cta', 'Get Started Free')}
                      <ArrowRight size={18} strokeWidth={2.5} />
                    </button>

                    <div className={styles.features}>
                      {tier.features.full.map((feature, idx) => (
                        <div key={idx} className={styles.feature}>
                          <div className={styles.checkIcon}>
                            <Check size={16} strokeWidth={2.5} />
                          </div>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Feature Comparison Table */}
          <section className={styles.comparisonSection}>
            <div className={styles.comparisonHeader}>
              <h2 className={styles.comparisonTitle}>
                {t('pricing.comparison.title', 'Compare All Features')}
              </h2>
              <p className={styles.comparisonSubtitle}>
                {t('pricing.comparison.subtitle', 'See exactly what\'s included in each plan')}
              </p>
            </div>

            <div className={styles.tableWrapper}>
              <table className={styles.comparisonTable}>
                <thead>
                  <tr>
                    <th className={styles.featureColumn}>
                      {t('pricing.comparison.feature', 'Feature')}
                    </th>
                    {ALL_TIERS.map((tierId) => (
                      <th key={tierId} className={styles.tierColumn}>
                        {PRICING_TIERS[tierId].name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_CATEGORIES.map((category, catIdx) => (
                    <React.Fragment key={catIdx}>
                      <tr className={styles.categoryRow}>
                        <td colSpan={5} className={styles.categoryName}>
                          {category.name}
                        </td>
                      </tr>
                      {category.features.map((feature, featIdx) => (
                        <tr key={featIdx} className={styles.featureRow}>
                          <td className={styles.featureName}>{feature.name}</td>
                          {ALL_TIERS.map((tierId) => {
                            const tierValue = Array.isArray(feature.tiers)
                              ? feature.tiers.includes(tierId)
                              : feature.tiers[tierId];

                            return (
                              <td key={tierId} className={styles.featureValue}>
                                {tierValue === true || (Array.isArray(feature.tiers) && feature.tiers.includes(tierId)) ? (
                                  <Check size={20} strokeWidth={2.5} className={styles.checkmark} />
                                ) : tierValue === false || tierValue === '—' ? (
                                  <X size={20} strokeWidth={2.5} className={styles.cross} />
                                ) : (
                                  <span className={styles.valueText}>{tierValue}</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Trust Section */}
          <section className={styles.trustSection}>
            <div className={styles.trustGrid}>
              <div className={styles.trustCard}>
                <div className={styles.trustIcon}>
                  <Shield size={24} strokeWidth={2} />
                </div>
                <h3 className={styles.trustTitle}>
                  {t('pricing.trust.security.title', '14-Day Money-Back Guarantee')}
                </h3>
                <p className={styles.trustDescription}>
                  {t('pricing.trust.security.description', 'Not satisfied? Get a full refund within 14 days, no questions asked.')}
                </p>
              </div>

              <div className={styles.trustCard}>
                <div className={styles.trustIcon}>
                  <Lock size={24} strokeWidth={2} />
                </div>
                <h3 className={styles.trustTitle}>
                  {t('pricing.trust.encryption.title', 'End-to-end Encryption')}
                </h3>
                <p className={styles.trustDescription}>
                  {t('pricing.trust.encryption.description', 'AES-256 encryption at rest and in transit. Your data is always secure.')}
                </p>
              </div>

              <div className={styles.trustCard}>
                <div className={styles.trustIcon}>
                  <Zap size={24} strokeWidth={2} />
                </div>
                <h3 className={styles.trustTitle}>
                  {t('pricing.trust.instant.title', 'Instant Activation')}
                </h3>
                <p className={styles.trustDescription}>
                  {t('pricing.trust.instant.description', 'Start using all features immediately. No waiting, no approval process.')}
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className={styles.faqSection}>
            <div className={styles.faqHeader}>
              <h2 className={styles.faqTitle}>
                {t('pricing.faq.title', 'Frequently Asked Questions')}
              </h2>
            </div>

            <div className={styles.faqGrid}>
              {FAQ_ITEMS.map((item, idx) => (
                <div key={idx} className={styles.faqItem}>
                  <h3 className={styles.faqQuestion}>{item.question}</h3>
                  <p className={styles.faqAnswer}>{item.answer}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Final CTA */}
          <section className={styles.finalCTA}>
            <h2 className={styles.finalCTATitle}>
              {t('pricing.final_cta.title', 'Ready to take control of your insurance?')}
            </h2>
            <p className={styles.finalCTASubtitle}>
              {t('pricing.final_cta.subtitle', 'Join 10,000+ Canadians who already know exactly what their policies cover.')}
            </p>
            <button className={styles.finalCTAButton} onClick={handleGetStarted}>
              {t('pricing.final_cta.button', 'Get Started Free')}
              <ArrowRight size={20} strokeWidth={2.5} />
            </button>
          </section>
        </div>

        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialType="sign_up"
        />
      </PageContainer>
    </>
  );
};

export default Pricing;